"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.occurrences = occurrences;
exports.lineItemSpan = lineItemSpan;
exports.lineItemMoneyPeriods = lineItemMoneyPeriods;
exports.occurrenceState = occurrenceState;
exports.occurrenceOf = occurrenceOf;
const dates_1 = require("../dates");
/**
 * ⚠️⚠️ **AN OPEN-ENDED RECURRENCE HAS TO STOP SOMEWHERE, AND THE MOCKUP'S NUMBERS ARE COPIED
 * RATHER THAN CHOSEN.** A framework contract with no end date generates periods for ever; the
 * prototype renders two years of months or two years of weeks and stops. That is a rendering
 * limit, not a business rule — the contract does not end, the list does.
 *
 * ⚠️⚠️ **IT IS ALSO THE NOMINAL SPAN OF AN OPEN-ENDED LINE ITEM (`decisions.md` P2-33, U-5).** A
 * line item's estimate, budget and revenue are stored per period, and its TOTAL is that × the
 * periods it spans; for an open-ended item that count is these 24 or 104, whatever `today` is, so
 * the total a screen shows never grows because the calendar moved on.
 */
const OPEN_ENDED_LIMIT = { month: 24, week: 104 };
/**
 * ⚠️⚠️ **GIVEN `today`, AN OPEN-ENDED SERIES REACHES THIS MANY PERIODS PAST IT — P2-33.** Counting
 * the 24 from the start meant a framework contract signed two years ago had no current period at
 * all: its worklogs fell outside every row, and nothing could be invoiced or costed against this
 * month. A year ahead is what a planner looks at.
 */
const OPEN_ENDED_AHEAD = { month: 12, week: 52 };
/** An open-ended series that reaches today stops on the calendar; this only stops a runaway. */
const OPEN_ENDED_RUNAWAY = 5000;
/** With a hard end the loop stops on the date; this only stops a runaway. */
const HARD_END_LIMIT = 400;
/** `count` mode is bounded by the schema (`1..200`); this mirrors it so the engine cannot exceed it. */
const COUNT_LIMIT = 200;
/**
 * The periods a recurrence produces.
 *
 * @param recurrence the contract's, or `null` for a contract that is not recurring — which yields
 *   an empty list rather than throwing, because *"has no periods"* is the honest answer for a
 *   milestone contract and every caller would otherwise branch first.
 * @param contractEndsOn the contract's own end. ⚠️ **It truncates even in `count` mode**: a
 *   contract that ends in June does not accrue a period in July because somebody typed 12.
 * @param today when given, an open-ended series runs on to `OPEN_ENDED_AHEAD` periods past the one
 *   it falls in. ⚠️ **Without it the list is the NOMINAL series**, which is what a line item's
 *   money and estimate are spread over and must not move with the date (P2-33). A series with a
 *   hard end or a count ignores it.
 */
function occurrences(recurrence, contractEndsOn, today = null) {
    if (recurrence === null)
        return [];
    const step = Math.max(1, Math.trunc(recurrence.step) || 1);
    const start = recurrence.startsOn;
    if (start === '')
        return [];
    /**
     * ⚠️ **The hard end is the RECURRENCE's when it has one, and the CONTRACT's otherwise** — and the
     * contract's still wins when it is earlier. The two are usually the same date; when they differ,
     * the contract is the outer bound, because a recurrence cannot outlive the agreement it is part
     * of.
     */
    const recurrenceEnd = recurrence.endMode === 'date' ? recurrence.endsOn : null;
    const hardEnd = earliest(recurrenceEnd, contractEndsOn);
    const counted = recurrence.endMode === 'count' && recurrence.count !== null;
    const openEnded = !counted && hardEnd === null;
    const reachesToday = openEnded && today !== null;
    const nominal = OPEN_ENDED_LIMIT[recurrence.unit];
    const limit = counted
        ? Math.min(COUNT_LIMIT, Math.max(1, Math.trunc(recurrence.count ?? 1)))
        : hardEnd !== null
            ? HARD_END_LIMIT
            : reachesToday
                ? OPEN_ENDED_RUNAWAY
                : nominal;
    const out = [];
    let cursor = start;
    let ahead = 0;
    for (let i = 0; i < limit; i += 1) {
        if (hardEnd !== null && cursor > hardEnd)
            break;
        /* ⚠️ Never fewer than the nominal series, so `today` only ever ADDS periods to the end. */
        if (reachesToday && i >= nominal && ahead >= OPEN_ENDED_AHEAD[recurrence.unit])
            break;
        if (today !== null && cursor > today)
            ahead += 1;
        /**
         * ⚠️⚠️ **EACH BOUNDARY IS COMPUTED FROM `start`, NEVER FROM THE PREVIOUS ONE.** Stepping a
         * month at a time from the last boundary drifts: 31 January + 1 month is 28 February, and
         * +1 again is 28 March rather than 31 March. Multiplying the offset from the original start
         * keeps every period anchored to the day of the month somebody actually chose.
         */
        const next = recurrence.unit === 'week'
            ? (0, dates_1.addWireDays)(start, 7 * step * (i + 1))
            : addWireMonths(start, step * (i + 1));
        if (next === null)
            break;
        const naturalTo = (0, dates_1.addWireDays)(next, -1);
        if (naturalTo === null)
            break;
        const to = hardEnd !== null && naturalTo > hardEnd ? hardEnd : naturalTo;
        out.push({ index: i + 1, from: cursor, to, naturalTo, partial: to !== naturalTo });
        cursor = next;
    }
    return out;
}
/**
 * How many periods a line item spans — what its per-period estimate, budget and revenue are
 * multiplied by to give its total (P2-11, U-5).
 *
 * ⚠️⚠️ **NEVER THE LENGTH OF A LIST THAT GROWS WITH `today` — `decisions.md` P2-33.** The periods a
 * screen shows run a year past today; multiplying by them would grow a retainer's total a little
 * further each month, with nothing on the screen to say why.
 *
 * - **An open-ended item on an open-ended series** spans the nominal 24 months or 104 weeks,
 *   wherever it starts — the count the line item table already names in its per-period note.
 * - **Anything with an end** spans the periods it overlaps, counted on a series that reaches that
 *   end however far out it is.
 *
 * Zero when it overlaps none; `lineItemMoneyPeriods` floors that at one.
 */
function lineItemSpan(recurrence, contractEndsOn, item) {
    if (recurrence === null)
        return 0;
    const openEnded = !(recurrence.endMode === 'count' && recurrence.count !== null) &&
        (recurrence.endMode === 'date' ? recurrence.endsOn : null) === null &&
        contractEndsOn === null;
    if (openEnded && item.endsOn === null)
        return OPEN_ENDED_LIMIT[recurrence.unit];
    const series = occurrences(recurrence, contractEndsOn, openEnded ? item.endsOn : null);
    return series.filter((one) => {
        const from = item.startsOn ?? one.from;
        const to = item.endsOn ?? one.to;
        return one.to >= from && one.from <= to;
    }).length;
}
/**
 * ⚠️⚠️ **U-5 · HOW MANY UNITS OF A LINE ITEM'S STORED MONEY MAKE ITS TOTAL.** On a recurring
 * contract the stored budget and revenue are per period, so the total is × the item's span
 * (`lineItemSpan`, floored at one). Off one — `recurrence` null — the stored figure IS the total,
 * and the answer is 1. Every reader that wants a line item's whole agreement multiplies by this,
 * so the two cases are one call rather than a branch at each site.
 *
 * ⚠️ **SQL twin: `0212_a_recurring_line_items_money_is_per_period.sql`** converted the stored
 * totals with the same count; the two must agree, and the migration's own test holds them.
 */
function lineItemMoneyPeriods(recurrence, contractEndsOn, item) {
    if (recurrence === null)
        return 1;
    return Math.max(1, lineItemSpan(recurrence, contractEndsOn, item));
}
/**
 * ⚠️⚠️ **THE CALENDAR DECIDES, NOT A COLUMN.** A period opens because its first day arrived and
 * closes because its last one passed — nobody clicks anything, and there is no state to get out of
 * sync with the dates. The one human input the mockup allows is *reopening* a closed period for
 * late entry, which is a separate flag over this, not a replacement for it.
 */
function occurrenceState(occurrence, today) {
    if (occurrence.to < today)
        return 'closed';
    if (occurrence.from <= today)
        return 'active';
    return 'planned';
}
/** The period a date falls in, or null when it falls outside every one of them. */
function occurrenceOf(occurrences, day) {
    return occurrences.find((one) => day >= one.from && day <= one.to) ?? null;
}
/**
 * `start` plus n months, clamped to the end of the target month.
 *
 * ⚠️ **31 January + 1 month is 28 February, not 3 March.** `new Date(y, m + 1, 31)` rolls over,
 * which would put a period boundary in the wrong month and silently shift every one after it.
 * `dates.ts` has no month helper for exactly this reason — the rule is not obvious enough to
 * inline, and this is its only caller.
 */
function addWireMonths(wire, months) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(wire);
    if (!match)
        return null;
    const year = Number(match[1]);
    const month = Number(match[2]) - 1;
    const day = Number(match[3]);
    /* Day 0 of the following month is the last day of the target month. */
    const lastDay = new Date(year, month + months + 1, 0).getDate();
    return (0, dates_1.toWireDate)(new Date(year, month + months, Math.min(day, lastDay)));
}
function earliest(a, b) {
    if (a === null)
        return b;
    if (b === null)
        return a;
    return a < b ? a : b;
}
//# sourceMappingURL=occurrences.js.map